import _ from 'lodash'
import React, { useContext, useState, useMemo } from 'react'
import {
  IPersonSkill,
  IPersonIndustry,
  IPersonRole,
  IEmployer,
  IProject,
  IEducation,
  ICourse,
  IPersonCertificate,
  IPersonCompetence,
  IReference,
  ILeisureActivity,
  ILanguageSkill,
  IUrl,
} from 'types/cvsInterfaces'
import { ICVData, IItemVisibility, ILayout, IVisibilities } from 'types/layoutInterfaces'

interface PrintPreviewState {
  visibleLayout?: ILayout
  setVisibleLayout: (newValue: ILayout | undefined) => void
  visibleData?: ICVData
  data?: ICVData
  setData: (newValue: ICVData | undefined) => void
}

const printContext = React.createContext<PrintPreviewState>({} as PrintPreviewState)

const { Provider } = printContext

/**
 * Generates the visible data for the PrintPreviewProvider component based on the provided layout and data.
 *
 * @param {ICVData} data - The input data for the PrintPreviewProvider component.
 * @param {ILayout} visibleLayout - The layout containing the item visibilities.
 * @return {ICVData | undefined} The visible data for the PrintPreviewProvider component.
 * @notExported
 */
export const PrintPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visibleLayout, setVisibleLayout] = useState<ILayout>()
  const [data, setData] = useState<ICVData>()

  const visibilities = useMemo(() => {
    const calculateVisibilities = (ItemVisibilities: IItemVisibility[]): IVisibilities => {
      if (ItemVisibilities.length < 1) {
        return {
          skill: [],
          industry: [],
          role: [],
          employment: [],
          project: [],
          degree: [],
          course: [],
          personCertificate: [],
          personCompetence: [],
          reference: [],
          language: [],
          url: [],
          leisureActivity: [],
        }
      }

      return {
        skill: ItemVisibilities.filter(visibility => visibility.type === 'skills'),
        industry: ItemVisibilities.filter(visibility => visibility.type === 'industries'),
        role: ItemVisibilities.filter(visibility => visibility.type === 'roles'),
        employment: ItemVisibilities.filter(visibility => visibility.type === 'employments'),
        project: ItemVisibilities.filter(visibility => visibility.type === 'projects'),
        degree: ItemVisibilities.filter(visibility => visibility.type === 'degrees'),
        course: ItemVisibilities.filter(visibility => visibility.type === 'courses'),
        personCertificate: ItemVisibilities.filter(visibility => visibility.type === 'certificates'),
        personCompetence: ItemVisibilities.filter(visibility => visibility.type === 'competences'),
        reference: ItemVisibilities.filter(visibility => visibility.type === 'references'),
        language: ItemVisibilities.filter(visibility => visibility.type === 'languages'),
        url: ItemVisibilities.filter(visibility => visibility.type === 'urls'),
        leisureActivity: ItemVisibilities.filter(visibility => visibility.type === 'leisureActivities'),
      }
    }

    if (visibleLayout?.ItemVisibilities) {
      return calculateVisibilities(visibleLayout.ItemVisibilities)
    }
  }, [visibleLayout?.ItemVisibilities])

  const visibleData: ICVData | undefined = useMemo(() => {
    const getVisibleItems = <T extends { id: number }>(visibilities: IItemVisibility[], items: T[]): T[] => {
      const visibleItems: unknown[] = []
      for (const item of items) {
        const visibility = visibilities.find(x => x.itemId === item.id)
        if (visibility && !visibility.hidden) {
          visibleItems.push(item)
        }
        if (!visibility) {
          visibleItems.push(item)
        }
      }

      return visibleItems as T[]
    }

    const calculateVisibleData = (data: ICVData, visibilities: IVisibilities): ICVData => {
      let visibilitiesLength = 0
      for (const key of Object.keys(visibilities) as Array<keyof IVisibilities>) {
        visibilitiesLength += visibilities[key].length
      }

      if (!visibilitiesLength) {
        return data
      }

      const visibleData: ICVData = data

      if (data.skills.length && visibilities.skill.length) {
        visibleData.skills = getVisibleItems(visibilities.skill, data.skills) as IPersonSkill[]
      }
      if (data.industries.length && visibilities.industry.length) {
        visibleData.industries = getVisibleItems(visibilities.industry, data.industries) as IPersonIndustry[]
      }
      if (data.roles.length && visibilities.role.length) {
        visibleData.roles = getVisibleItems(visibilities.role, data.roles) as IPersonRole[]
      }
      if (data.employments.length && visibilities.employment.length) {
        visibleData.employments = getVisibleItems(visibilities.employment, data.employments) as IEmployer[]
      }
      if (data.projects.length && visibilities.project.length) {
        visibleData.projects = getVisibleItems(visibilities.project, data.projects) as IProject[]
      }
      if (data.degrees.length && visibilities.degree.length) {
        visibleData.degrees = getVisibleItems(visibilities.degree, data.degrees) as IEducation[]
      }
      if (data.courses.length && visibilities.course.length) {
        visibleData.courses = getVisibleItems(visibilities.course, data.courses) as ICourse[]
      }
      if (data.personCertificates.length && visibilities.personCertificate.length) {
        visibleData.personCertificates = getVisibleItems(
          visibilities.personCertificate,
          data.personCertificates
        ) as IPersonCertificate[]
      }
      if (data.personCompetences.length && visibilities.personCompetence.length) {
        visibleData.personCompetences = getVisibleItems(
          visibilities.personCompetence,
          data.personCompetences
        ) as IPersonCompetence[]
      }
      if (data.references.length && visibilities.reference.length) {
        visibleData.references = getVisibleItems(visibilities.reference, data.references) as IReference[]
      }
      if (data.languages.length && visibilities.language.length) {
        visibleData.languages = getVisibleItems(visibilities.language, data.languages) as ILanguageSkill[]
      }
      if (data.urls.length && visibilities.url.length) {
        visibleData.urls = getVisibleItems(visibilities.url, data.urls) as IUrl[]
      }
      if ((data.leisureActivities?.length ?? 0) > 0 && visibilities.leisureActivity.length) {
        visibleData.leisureActivities = getVisibleItems(
          visibilities.leisureActivity,
          data.leisureActivities ?? []
        ) as ILeisureActivity[]
      }

      return visibleData
    }

    if (visibilities && data) {
      return calculateVisibleData(_.cloneDeep(data), visibilities)
    }
  }, [visibilities, data])

  return (
    <Provider
      value={{
        visibleLayout,
        setVisibleLayout,
        visibleData,
        data,
        setData,
      }}
    >
      {children}
    </Provider>
  )
}

export function usePreviewData(): PrintPreviewState {
  return useContext(printContext)
}
